import React, { useEffect, useState, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import logo from '../../images/Sapotai.png';
import { useT, useLang } from '../../i18n/LanguageContext';
import strings from './Navbar.i18n';
import './Navbar.css';

gsap.registerPlugin(ScrollTrigger);

export default function Navbar({ currentPage, isScrolled, mobileMenuOpen, setMobileMenuOpen, onNavigate }) {
  const [navTheme, setNavTheme] = useState('default');
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const navRef = useRef(null);
  const t = useT(strings);
  const { lang, setLang } = useLang();

  const navLinks = t.navLinks;
  const productDropdownItems = t.productDropdownItems;

  // Close mobile menu when viewport exceeds mobile breakpoint
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setMobileMenuOpen(false);
        setMobileDropdownOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setMobileMenuOpen]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setMobileDropdownOpen(false);
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  useEffect(() => {
    // Reset to default on every page change so the navbar
    // doesn't stay inverted when navigating to a page without dark sections
    setNavTheme('default');

    const darkSections = document.querySelectorAll('[data-nav-theme="dark"]');
    if (darkSections.length === 0) return;

    const triggers = [];
    darkSections.forEach((section) => {
      const st = ScrollTrigger.create({
        trigger: section,
        start: 'top 64px',
        end: 'bottom 64px',
        onEnter: () => setNavTheme('inverted'),
        onLeaveBack: () => setNavTheme('default'),
        onLeave: () => setNavTheme('default'),
        onEnterBack: () => setNavTheme('inverted'),
      });
      triggers.push(st);
    });

    return () => triggers.forEach((st) => st.kill());
  }, [currentPage]);

  const handleMobileLinkClick = (link) => {
    onNavigate(link.id);
    setMobileMenuOpen(false);
  };

  const handleMobileDropdownToggle = (e) => {
    e.stopPropagation();
    setMobileDropdownOpen(!mobileDropdownOpen);
  };

  const LanguageToggle = ({ className = '' }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
      if (!open) return;
      const handleClickOutside = (e) => {
        if (ref.current && !ref.current.contains(e.target)) setOpen(false);
      };
      const handleKey = (e) => {
        if (e.key === 'Escape') setOpen(false);
      };
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKey);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleKey);
      };
    }, [open]);

    const options = [
      { value: 'en', label: 'English' },
      { value: 'zh', label: '中文' },
    ];

    const handleSelect = (value) => {
      setLang(value);
      setOpen(false);
    };

    return (
      <div
        ref={ref}
        className={`navbar-lang-menu ${className}`.trim()}
      >
        <button
          type="button"
          className="navbar-lang-menu__trigger"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={t.languageToggleLabel}
        >
          <svg
            className="navbar-lang-menu__icon"
            viewBox="0 0 73.768 73.768"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M117.606,385.2a36.884,36.884,0,1,0,36.884,36.884A36.926,36.926,0,0,0,117.606,385.2Zm33.846,35.383H136.366a48.681,48.681,0,0,0-3.047-16.068,36.786,36.786,0,0,0,8.781-5.808A33.752,33.752,0,0,1,151.452,420.586Zm-32.346-31.072a36.534,36.534,0,0,1,6.069,6.387,39.467,39.467,0,0,1,4.176,7.028,33.843,33.843,0,0,1-10.245,2.061Zm3.534-.935a33.762,33.762,0,0,1,17.292,8.051,33.809,33.809,0,0,1-7.772,5.116A41.252,41.252,0,0,0,122.64,388.579ZM110.19,395.9a36.615,36.615,0,0,1,5.916-6.261v15.35a33.789,33.789,0,0,1-10.116-2.013A39.5,39.5,0,0,1,110.19,395.9Zm-7.013,5.906a33.8,33.8,0,0,1-7.9-5.177,33.757,33.757,0,0,1,17.469-8.074A41.244,41.244,0,0,0,103.177,401.807Zm12.929,6.183v12.6H102a45.607,45.607,0,0,1,2.835-14.838A36.83,36.83,0,0,0,116.106,407.99Zm0,15.6v12.386a36.8,36.8,0,0,0-11.018,2.146A42.373,42.373,0,0,1,102,423.587Zm0,15.386v15.252a47.106,47.106,0,0,1-9.792-13.361A33.819,33.819,0,0,1,116.106,438.973Zm-2.86,16.708a33.755,33.755,0,0,1-18.084-8.24,33.786,33.786,0,0,1,8.306-5.426A48.955,48.955,0,0,0,113.246,455.681Zm5.86-1.313v-15.4a33.8,33.8,0,0,1,9.922,1.94A47.081,47.081,0,0,1,119.106,454.368Zm12.762-12.294a33.846,33.846,0,0,1,8.182,5.367,33.759,33.759,0,0,1-17.909,8.217A48.888,48.888,0,0,0,131.868,442.074Zm-12.762-6.1V423.587h14.257a42.352,42.352,0,0,1-3.106,14.582A36.818,36.818,0,0,0,119.106,435.973Zm0-15.386v-12.6a36.806,36.806,0,0,0,11.4-2.291,45.562,45.562,0,0,1,2.854,14.888ZM93.112,398.711a36.8,36.8,0,0,0,8.91,5.871A48.7,48.7,0,0,0,99,420.587H83.76A33.757,33.757,0,0,1,93.112,398.711ZM83.76,423.587H99a45.675,45.675,0,0,0,3.256,15.683A36.807,36.807,0,0,0,93,445.35,33.755,33.755,0,0,1,83.76,423.587Zm58.447,21.764a36.8,36.8,0,0,0-9.122-6.022,45.69,45.69,0,0,0,3.279-15.742h15.088A33.759,33.759,0,0,1,142.207,445.351Z"
              transform="translate(-80.722 -385.203)"
              fill="currentColor"
            />
          </svg>
        </button>
        {open && (
          <ul className="navbar-lang-menu__list" role="listbox">
            {options.map((opt) => (
              <li key={opt.value} role="none">
                <button
                  type="button"
                  role="option"
                  aria-selected={lang === opt.value}
                  className={`navbar-lang-menu__item${lang === opt.value ? ' navbar-lang-menu__item--active' : ''}`}
                  onClick={() => handleSelect(opt.value)}
                >
                  {opt.label}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  return (
    <>
      <nav
        ref={navRef}
        className={`navbar${isScrolled ? ' navbar-scrolled' : ''}`}
        data-nav-theme={navTheme}
      >
        <div className="navbar-inner">
          <div className="navbar-brand" onClick={() => onNavigate('home')}>
            <img src={logo} alt="SAPOT AI" className="navbar-logo" />
          </div>

          {/* Desktop nav links */}
          <div className="navbar-links">
            {navLinks.map((link) => (
              <div
                key={link.id}
                className={`navbar-link-wrapper${link.hasDropdown ? ' navbar-link-wrapper--has-dropdown' : ''}`}
              >
                <button
                  className={`navbar-link${currentPage === link.id ? ' navbar-link-active' : ''}${link.hasDropdown ? ' navbar-link--with-arrow' : ''}`}
                  onClick={() => onNavigate(link.id)}
                >
                  {link.label}
                  {link.hasDropdown && <span className="navbar-link__arrow">&#9662;</span>}
                </button>

                {link.hasDropdown && (
                  <div className="navbar-dropdown">
                    <div className="navbar-dropdown__inner">
                      {productDropdownItems.map((item) => (
                        <button
                          key={item.title}
                          className={`navbar-dropdown__item${currentPage === item.page ? ' navbar-dropdown__item--active' : ''}`}
                          onClick={() => {
                            onNavigate(item.page);
                            setMobileMenuOpen(false);
                          }}
                        >
                          <span className="navbar-dropdown__icon">{item.icon}</span>
                          <div className="navbar-dropdown__text">
                            <span className="navbar-dropdown__title">{item.title}</span>
                            <span className="navbar-dropdown__desc">{item.desc}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="navbar-actions">
            <LanguageToggle className="navbar-lang-menu--desktop" />
            <a
              href="https://app.sapot.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="navbar-app-link navbar-app-link--desktop"
            >
              {t.goToApp}
            </a>
            <LanguageToggle className="navbar-lang-menu--mobile-inline" />
            <button
              className="navbar-cta navbar-cta-desktop"
              onClick={() => window.open('https://wa.me/601163688943?text=Hi%2C%20I%20would%20like%20to%20get%20started%20with%20SAPOT%20AI', '_blank')}
            >
              {t.getStartedFree}
            </button>

            <button
              className={`navbar-toggle${mobileMenuOpen ? ' navbar-toggle-active' : ''}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={t.toggleMenu}
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile full-screen overlay */}
      <div className={`mobile-overlay${mobileMenuOpen ? ' mobile-overlay--open' : ''}`}>
        <div className="mobile-overlay__header">
          <div className="navbar-brand" onClick={() => { onNavigate('home'); setMobileMenuOpen(false); }}>
            <img src={logo} alt="SAPOT AI" className="navbar-logo" />
          </div>
          <button
            className="mobile-overlay__close"
            onClick={() => setMobileMenuOpen(false)}
            aria-label={t.closeMenu}
          >
            &#10005;
          </button>
        </div>

        <div className="mobile-overlay__links">
          {navLinks.map((link) => (
            <div key={link.id} className="mobile-overlay__link-group">
              <div className="mobile-overlay__link-row">
                <button
                  className={`mobile-overlay__link${currentPage === link.id ? ' mobile-overlay__link--active' : ''}`}
                  onClick={() => handleMobileLinkClick(link)}
                >
                  {link.label}
                </button>
                {link.hasDropdown && (
                  <button
                    className={`mobile-overlay__arrow-btn${mobileDropdownOpen ? ' mobile-overlay__arrow-btn--open' : ''}`}
                    onClick={handleMobileDropdownToggle}
                    aria-label="Toggle product submenu"
                  >
                    <span className="mobile-overlay__arrow">&#9662;</span>
                  </button>
                )}
              </div>

              {link.hasDropdown && mobileDropdownOpen && (
                <div className="mobile-overlay__dropdown">
                  {productDropdownItems.map((item) => (
                    <button
                      key={item.title}
                      className={`mobile-overlay__dropdown-item${currentPage === item.page ? ' mobile-overlay__dropdown-item--active' : ''}`}
                      onClick={() => { onNavigate(item.page); setMobileMenuOpen(false); }}
                    >
                      <span className="mobile-overlay__dropdown-icon">{item.icon}</span>
                      {item.title}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mobile-overlay__footer">
          <button
            className="mobile-overlay__cta"
            onClick={() => {
              window.open('https://wa.me/601163688943?text=Hi%2C%20I%20would%20like%20to%20get%20started%20with%20SAPOT%20AI', '_blank');
              setMobileMenuOpen(false);
            }}
          >
            {t.getStartedFree}
          </button>
        </div>
      </div>
    </>
  );
}
